import AnimatedPopup from "../core/AnimatedPopup";
import ARIAText from "../core/ARIAText";
import Strings from "../core/data/Strings";
import GameScene from "../scenes/GameScene";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";
import LayoutUtils from "../utils/LayoutUtils";
import Overlay from "../utils/Overlay";

export class TutorialPopup extends AnimatedPopup {
	private _onResize: () => void;
	scene: GameScene;
	onComplete: () => void;
	index = 1;
	helpTips: Phaser.GameObjects.Container[] = [];
	hand: Phaser.GameObjects.Image;

	constructor(scene: GameScene, onComplete: () => void, tutorialStart = 1, tutorialEnd = 3) {
		super(scene, 0, 0);
		this.onComplete = onComplete;
		const overlay = (this._overlay = new Overlay(scene, 0.7, "popupOverlay", 0xffffff));
		this.add(overlay);

		const popupGroup = (this._popupGroup = this.scene.add.container());
		this.add(popupGroup);

		const popup = scene.add.nineSlicedSprite(0, 0, "popupBG", 1325, 920, 50, 50, 50, 50, null, false);
		popupGroup.add(popup);
		popupGroup.setSize(popup.slicedWidth, popup.slicedHeight);

		const headerBG = scene.add.image(0, 0, "popupHeader");
		const headerText = this.scene.add.text(0, 0, Strings.get("HowToPlay").toUpperCase(), {
			color: GameColors.WHITE,
			fontSize: "70px",
			fontFamily: Fonts.MontserratExtraBold,
			align: "center",
		});
		popupGroup.add([headerBG, headerText]);
		this.scene.alignIn.TopCenter(headerText, popup, 0, -25);

		const button = scene.add.button(0, 0, "nextBtn", null, () => this.nextPage(), null, true);
		popupGroup.add(button);

		//const helpTips: Phaser.GameObjects.Container[] = [];
		for (let i = tutorialStart; i <= tutorialEnd; i++) {
			let flip = i % 2 == 0;
			if (i == 4) flip = false;
			const tip = this.createContent(i, flip);
			this.helpTips.push(tip);
			if (tutorialStart == 1) {
				if (i >= 2) tip.alpha = 0;
			}
			popupGroup.add(tip);
		}

		const hand = (this.hand = this.scene.add.image(0, 0, "helpHand"));
		this.scene.add.tween({
			targets: hand,
			scale: { from: 1.2, to: 1 },
			duration: 400,
			yoyo: true,
			repeat: -1,
			repeatDelay: 200,
			delay: 300,
			ease: Phaser.Math.Easing.Quadratic.In,
		});
		popupGroup.add(hand);
		hand.setVisible(false);

		this._onResize = (): void => {
			scene.alignIn.Center(popupGroup, scene.viewport);
			scene.alignIn.TopCenter(headerBG, popup, 0, headerBG.height / 2);
			scene.alignIn.Center(headerText, headerBG, 0, 0);

			scene.alignTo.BottomCenter(button, popup, 0, -button.height * 0.75);

			const tipSize = new Phaser.Math.Vector2(
				this.helpTips[this.helpTips.length - 1].displayWidth,
				this.helpTips[0].displayHeight / 2
			);
			LayoutUtils.alignGrid(this.scene, this.helpTips, popup, tipSize, 4, 0, -15);

			for (let i = 0; i < this.helpTips.length; i++) {
				if (i % 2 == 0) {
					this.helpTips[i].y -= 150;
				}
			}
			if (this.helpTips.length < 3) {
				this.helpTips[1].y -= 150;
				scene.alignIn.Center(hand, this.helpTips[0], 95, -20);
			} else scene.alignIn.Center(hand, this.helpTips[2], 95, -20);
		};
		this.resize();

		const ariaLayer = "tutorial-popup";
		headerText.setARIA(ariaLayer, true);
		this.helpTips.forEach((c) => {
			const text = c.getAt(4) as ARIAText;
			text.setARIA(ariaLayer);
		});
		button.setARIA("Next", ariaLayer);
		ARIA.showLayer(ariaLayer, true);

		// Hide Text ARIA after showing the layer
		if (tutorialStart == 1) {
			for (let i = 1; i < this.helpTips.length; i++) {
				const text = this.helpTips[i].getAt(4) as ARIAText;
				text.hideARIA();
			}
		}
		this.once(Phaser.GameObjects.Events.DESTROY, () => ARIA.showLayer(ARIA.DEFAULT_LAYER, true));

		this.intro();
		if (tutorialEnd == 5) this.index++;
	}

	createContent(index: number, textOnTop: boolean): Phaser.GameObjects.Container {
		const container = this.scene.add.container(0, 0);
		const image = this.scene.add.image(0, 0, "helpImage" + index);
		const numberBG = this.scene.add.image(0, 0, "numberBG");

		let number = index;
		if (index > 3) number -= 3;
		const numberText = this.scene.add.text(0, 0, number.toString(), {
			color: "#686ab5",
			fontSize: "42px",
			fontFamily: Fonts.MontserratExtraBold,
			align: "center",
		});
		let textBG: Phaser.GameObjects.Image;
		if (index != 5) textBG = this.scene.add.image(0, 0, "smallTextBG");
		else textBG = this.scene.add.image(0, 0, "longTextBG");
		const text = this.scene.add.text(0, 0, Strings.get("HelpTip" + index), {
			color: "#686ab5",
			fontSize: "30px",
			fontFamily: Fonts.MontserratExtraBold,
			align: "center",
			wordWrap: { width: textBG.displayWidth },
		});
		container.add([image, numberBG, numberText, textBG, text]);

		this.scene.alignIn.TopCenter(numberBG, image, 0, -30);
		this.scene.alignIn.Center(numberText, numberBG);

		if (textOnTop) this.scene.alignTo.TopCenter(textBG, image, 0, -20);
		else this.scene.alignTo.BottomCenter(textBG, image, 0, -30);

		this.scene.alignIn.Center(text, textBG, 5);

		container.setSize(textBG.width, image.height + textBG.height);
		return container;
	}

	nextPage(): void {
		if (this.helpTips[this.index] != undefined) {
			this.helpTips[this.index].alpha = 1;
			const text = this.helpTips[this.index].getAt(4) as ARIAText;
			text.showARIA();
			text.focusARIA();
		}
		if (this.index + 1 < this.helpTips.length) this.helpTips[this.index + 1].alpha = 0;
		this.index++;
		console.log("index: " + this.index);
		if (this.index == 3) this.hand.setVisible(true);
		if (this.index > this.helpTips.length) {
			//console.log("destroy()");
			this.destroy();
			this.onComplete();
		}
	}

	resize(): void {
		if (this._onResize) this._onResize();
		super.resize();
	}
}
